import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../lib/authApi';
import { useAuth } from '../context/AuthContext';
import AuthShell from '../components/AuthShell';

/** Handles the `#session_id=...` redirect back from Emergent Google auth. */
export default function AuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false); // ref (not state) — synchronous, StrictMode-safe
  const [status, setStatus] = useState('processing'); // processing | pending | error
  const [message, setMessage] = useState('Signing you in…');

  useEffect(() => {
    // CRITICAL: idempotent guard — set synchronously to avoid double-fire in dev StrictMode.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { setStatus('error'); setMessage('No session token in URL.'); return; }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const { user, needs_approval } = await authApi.googleSession(sessionId);
        // Clean the URL fragment so a refresh doesn't re-trigger the exchange.
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch { /* noop */ }
        if (needs_approval || user?.status !== 'approved') {
          setStatus('pending');
          setMessage('Your account was created. An administrator will review and approve your access shortly.');
          return;
        }
        await refresh();
        navigate('/home', { replace: true });
      } catch (e) {
        setStatus('error');
        setMessage(e.response?.data?.detail || 'Could not complete sign-in. Please try again.');
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <AuthShell title={status === 'pending' ? 'Pending approval' : 'Signing you in'} subtitle="Google sign-in">
      <div className={`rounded-md p-4 text-[13px] border ${status === 'error' ? 'bg-[#FBE3D6] border-[#E8B79A] text-[#7A2E0F]' : 'bg-[#E5F0EA] border-[#4A7C5E]/40 text-[#2D5F4F]'}`} data-testid="callback-status">
        {message}
      </div>
      {status !== 'processing' && (
        <button onClick={() => navigate('/login', { replace: true })} className="mt-6 w-full h-11 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F]">
          Back to sign in
        </button>
      )}
    </AuthShell>
  );
}
